import { effectiveLabRows, hasApprovedLabResults, effectivePackageLabSummary } from './utils/approvedLabResults';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useRemarkSync } from 'react-remark';
import RetailIdExpandedDesignPage, {
  type CannabinoidRow,
  type CannabinoidDisplayMode,
  type TerpeneRow,
  type RetailIdExpandedDesignPageProps,
} from './landingPage/expandedDesign/RetailIdExpandedDesignPage';
import { parseCoaData } from './landingPage/coa/parseCoaData';
import { getMarketCustomization, resolveLandingMarketCode } from './landingPage/CustomLanding';
import { cannabinoidsMeta, ingredientsMeta, terpenesMeta, terpeneKeys } from '@wysiwyg/moss-runtime';
import { cannabinoidOverrideKey, terpeneOverrideKey, CANNABINOID_LABELS, TERPENE_LABELS } from './PackageLabResultsOverrides';
import { formatDate } from './utils/formatDate';
import { resolveLandingDates } from './landingPage/resolveLandingDates';
import { normalizePublicLandingUrl } from './landingPage/publicLandingUrl';
import { DescriptionFormat } from './landingPage/DescriptionFormat';
import { resolveProductFields } from './utils/resolveProductFields';
import type { ConfirmedDate } from './types/index';
import { parseLabMeasurement } from './utils/labMeasurement';
import { manualOverrideState, supportedTerpeneKey, manualCompoundSource, manualCompoundValue } from './utils/manualLabOverrides';
import { usePublicCoaFiles } from './hooks/usePublicCoaResults';
import type { MarketLandingPageLayout } from './landingPage/marketLandingPageLayout';

type LandingPageProps = {
  productCard?: any;
  brandCard?: any;
  coaCard?: any;
  regulatoryCard?: any;
  packageLabel?: string;
  isStaged?: boolean;
  stagedProducerName?: string | null;
  stagedProductName?: string | null;
  issuanceId?: string;
  landingIndex?: number;
  facilityName?: string;
  facilityLicense?: string;
  isOnRecall?: boolean;
  publicHealthNoticeUrls?: readonly string[] | null;
  marketCode?: string;
  confirmedDates?: ConfirmedDate[];
  layout?: MarketLandingPageLayout | null;
};

const str = (v: unknown) => String(v ?? '').trim();

function extractComplianceBodyName(description: string): string | undefined {
  if (!description) return undefined;
  const firstSentence = description.match(/^.*?[.!?](?:\s+|$)/)?.[0].trimEnd() ?? description;
  const parenIdx = firstSentence.indexOf('(');
  const isIdx = firstSentence.search(/\sis\s/);
  let end = firstSentence.length;
  if (parenIdx > 0) end = Math.min(end, parenIdx);
  if (isIdx > 0) end = Math.min(end, isIdx);
  return firstSentence.slice(0, end).trim() || undefined;
}

function descriptionPlainText(content: React.ReactNode): string {
  return React.Children.toArray(content).map((child): string => {
    if (typeof child === 'string' || typeof child === 'number') return String(child);
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return '';
    if (child.type === 'br') return '\n';
    return descriptionPlainText(child.props.children);
  }).join('');
}

// Agency labels use the first nonempty Markdown block; guidance remains in
// the full sanitized banner rendered separately by BannerIntroSection.
function descriptionFirstBlockText(content: React.ReactNode): string {
  for (const child of React.Children.toArray(content)) {
    const text = React.isValidElement<{ children?: React.ReactNode }>(child)
      && (child.type === React.Fragment || child.type === 'blockquote')
      ? descriptionFirstBlockText(child.props.children)
      : descriptionPlainText(child).trim();
    if (text) return text;
  }
  return '';
}

const greekPrefixes: Record<string, string> = {
  a: 'α', b: 'β', g: 'γ', d: 'δ',
};

function formatIngredientKey(key: string): string {
  const m = key.match(/^([abgd])([A-Z])/);
  if (m) {
    const greek = greekPrefixes[m[1]];
    const rest = key.slice(1).replace(/([A-Z])/g, ' $1').trim();
    return `${greek}-${rest}`;
  }
  if (/^delta\d/i.test(key)) {
    const rest = key.replace(/^delta/i, 'Δ').replace(/([A-Z])/g, ' $1').trim();
    return rest;
  }
  return key.replace(/([A-Z])/g, ' $1').trim();
}

function ensureCorrectFormat(label: string): string {
  return label
    .replace(/^([αβγδΔ])\s*[-–—]?\s*(\d)/, '$1$2')
    .replace(/^([αβγδΔ])\s*[-–—]?\s*([A-Za-z])/, '$1-$2');
}

function buildOverviewRows(coa: Record<string, unknown> | null, productCard: any, marketCode: string | undefined): { label: string; value: string; mono?: boolean }[] {
  const rows: { label: string; value: string; mono?: boolean }[] = [];
  const resolved = resolveProductFields(null, productCard, coa);
  if (resolved.productName) rows.push({ label: 'Name', value: resolved.productName });

  if (resolved.strain) rows.push({ label: 'Type', value: resolved.strain });

  const uid = str(coa?.id);
  if (uid) rows.push({ label: 'UID', value: uid, mono: true });

  const manufacturer = str((coa as any)?.manufacturer?.name);
  if (manufacturer) rows.push({ label: marketCode === 'CO' ? 'Produced by' : 'Manufactured By', value: manufacturer });

  if (marketCode === 'CO') {
    const license = str((coa as any)?.manufacturer?.licenseNumber);
    if (license) rows.push({ label: 'License Number', value: license });
  }

  return rows;
}

function buildLabTestStatus(coa: Record<string, unknown> | null, inferFromAnalytes: boolean): string | undefined {
  if (hasApprovedLabResults(coa)) return str(coa?.labTestingStateName) || undefined;
  const topLevel = str((coa as any)?.labTestStatus) || str((coa as any)?.labTestingStateName) || str((coa as any)?.status);
  if (topLevel) return topLevel;
  if (!inferFromAnalytes) return undefined;

  const labTests = (coa as any)?.labTests;
  if (!Array.isArray(labTests) || labTests.length === 0) return undefined;
  const allPassed = labTests.every((t: any) => str(t?.status).toLowerCase() === 'passed');
  return allPassed ? 'PASSED' : undefined;
}

function buildCannabinoids(
  coa: Record<string, unknown> | null,
  displayMode: CannabinoidDisplayMode,
  manualLabOverrides?: unknown,
): CannabinoidRow[] {
  const effectiveRows = effectiveLabRows(coa, 'cannabinoid', manualLabOverrides)
    // Reported dimensions (especially mg/unit) must not become package values.
    // Keep them visible beside the saved serving/package layout without conversion.
    .map(row => ({ ...row, weightModeResultText: displayMode === 'Weight'
      && (row.source === 'reported' || (hasApprovedLabResults(coa) && row.uom?.split('/')[1]?.toLowerCase() === 'unit'))
      ? row.source === 'reported' ? row.resultText : row.weightText : undefined }))
    .filter(row => row.weightModeResultText !== undefined || row.outcomeText !== undefined || (displayMode === 'Weight'
      ? row.perServingText !== undefined || row.perPackageText !== undefined
      : row.percentText !== undefined || (displayMode === 'Both' && row.weightText !== undefined)))
    .map(row => ({
      cannabinoid: row.label, weight: row.weightText ?? '', perServing: row.perServingText ?? '', perPackage: row.perPackageText ?? '',
      percent: row.percentText ?? '', outcomeText: row.outcomeText, weightModeResultText: row.weightModeResultText, emphasized: /total/i.test(row.key),
    }));
  if (hasApprovedLabResults(coa)) return effectiveRows;
  const rawIngredients = ((coa as any)?.ingredients ?? {}) as Record<string, any>;
  const terpeneKeySetLower = new Set(terpeneKeys.map(k => k.toLowerCase()));

  const legacyRows = Object.entries(rawIngredients)
    .filter(([key]) => !terpeneKeySetLower.has(key.toLowerCase()) && !supportedTerpeneKey(key)
      && typeof manualOverrideState(manualLabOverrides, cannabinoidOverrideKey(key).enabled) !== 'boolean')
    .map(([key, value]) => {
      const label = ensureCorrectFormat(cannabinoidsMeta[key]?.label ?? ingredientsMeta[key]?.label ?? CANNABINOID_LABELS[key] ?? formatIngredientKey(key));
      const weight = value?.weight ?? {};
      const percentValue = parseLabMeasurement(value?.percent) ?? parseLabMeasurement(weight?.percent);
      const unit = weight?.unit || 'mg';
      const perServingValue = parseLabMeasurement(weight?.srv);
      const perPackageValue = parseLabMeasurement(weight?.pkg);
      const perGramValue = parseLabMeasurement(weight?.amount);
      const perMlValue = parseLabMeasurement(weight?.ml);

      let weightValue: number | undefined;
      let uom = '';
      if (perServingValue) { weightValue = perServingValue; uom = `${unit}/srv`; }
      else if (perPackageValue) { weightValue = perPackageValue; uom = `${unit}/pkg`; }
      else if (perGramValue) { weightValue = perGramValue; uom = `${unit}/g`; }
      else if (perMlValue) { weightValue = perMlValue; uom = `${unit}/ml`; }

      const hasPercent = percentValue !== undefined && percentValue !== 0;
      const hasWeight = weightValue !== undefined && weightValue !== 0;
      const hasPerServing = perServingValue !== undefined && perServingValue !== 0;
      const hasPerPackage = perPackageValue !== undefined && perPackageValue !== 0;

      if (displayMode === 'Weight' && !hasPerServing && !hasPerPackage) return null;
      if (displayMode === 'Percent' && !hasPercent) return null;
      if (displayMode !== 'Weight' && !hasPercent && !hasWeight) return null;

      const isTotal = /total/i.test(key) || /total/i.test(label);
      return {
        cannabinoid: label,
        weight: hasWeight ? `${weightValue} ${uom}`.trim() : '',
        perServing: hasPerServing ? `${perServingValue} ${unit}` : '',
        perPackage: hasPerPackage ? `${perPackageValue} ${unit}` : '',
        percent: hasPercent ? `${percentValue}%` : hasWeight ? '' : 'ND',
        emphasized: isTotal,
      };
    })
    .filter(Boolean) as CannabinoidRow[];
  return [...legacyRows, ...effectiveRows]
    .sort((a, b) => {
      const pinnedOrder = ['total cannabinoids', 'total thc', 'total cbd'];
      const aIdx = pinnedOrder.findIndex(p => a!.cannabinoid.toLowerCase().replace(/ \(override\)$/, '') === p);
      const bIdx = pinnedOrder.findIndex(p => b!.cannabinoid.toLowerCase().replace(/ \(override\)$/, '') === p);
      const aPinned = aIdx !== -1;
      const bPinned = bIdx !== -1;
      if (aPinned && bPinned) return aIdx - bIdx;
      if (aPinned) return -1;
      if (bPinned) return 1;
      const aVal = parseFloat(a!.percent) || parseFloat(a!.weight) || 0;
      const bVal = parseFloat(b!.percent) || parseFloat(b!.weight) || 0;
      return bVal - aVal;
    }) as CannabinoidRow[];
}

function getCannabinoidDisplayMode(coa: Record<string, unknown> | null): CannabinoidDisplayMode {
  const mode = (coa as any)?.amountOptions;
  if (hasApprovedLabResults(coa) && mode !== 'Weight') return 'Both';
  return mode === 'Weight' || mode === 'Percent' || mode === 'Both' ? mode : 'Both';
}

export function normalizeTerpenePercent(value: unknown): number | undefined {
  return parseLabMeasurement(value);
}

function buildTerpenes(coa: Record<string, unknown> | null, manualLabOverrides?: unknown): TerpeneRow[] {
  const effectiveRows = effectiveLabRows(coa, 'terpene', manualLabOverrides).map(row => ({
    terpene: row.label, percent: row.resultText, reportedAmount: row.percent === undefined, emphasized: /total/i.test(row.key),
  }));
  if (hasApprovedLabResults(coa)) return effectiveRows;
  const terpeneKeyMap = new Map(terpeneKeys.map(k => [k.toLowerCase(), k]));
  const deduped: Record<string, any> = {};

  const rawTerpeneIngredients = ((coa as any)?.ingredients ?? {}) as Record<string, any>;
  for (const key of Object.keys(rawTerpeneIngredients)) {
    const canonical = supportedTerpeneKey(key) ?? terpeneKeyMap.get(key.toLowerCase());
    if (canonical) deduped[canonical] = manualCompoundValue(manualCompoundSource((coa as any)?.terpenes, rawTerpeneIngredients, terpeneOverrideKey(canonical).enabled), canonical, terpeneOverrideKey(canonical).enabled);
  }
  for (const [key, value] of Object.entries(((coa as any)?.terpenes ?? {}) as Record<string, any>)) {
    const supported = supportedTerpeneKey(key);
    const canonical = supported ?? terpeneKeyMap.get(key.toLowerCase()) ?? key;
    deduped[canonical] = supported
      ? manualCompoundValue(manualCompoundSource((coa as any)?.terpenes, rawTerpeneIngredients, terpeneOverrideKey(canonical).enabled), canonical, terpeneOverrideKey(canonical).enabled)
      : { ...(deduped[canonical] || {}), ...(value as any) };
  }

  const legacyRows = Object.entries(deduped)
    .filter(([key]) => typeof manualOverrideState(manualLabOverrides, terpeneOverrideKey(key).enabled) !== 'boolean')
    .map(([key, value]) => {
      const label = ensureCorrectFormat(terpenesMeta[key]?.label ?? TERPENE_LABELS[key] ?? formatIngredientKey(key));
      const percent = normalizeTerpenePercent(value?.percent);
      if (percent === undefined) return null;
      const isTotal = /total/i.test(key) || /total/i.test(label);
      return {
        terpene: label,
        percent: `${percent}%`,
        emphasized: isTotal,
      };
    })
    .filter(Boolean) as TerpeneRow[];
  return [...legacyRows, ...effectiveRows].sort((a, b) => {
      const isTotalA = /total/i.test(a!.terpene);
      const isTotalB = /total/i.test(b!.terpene);
      if (isTotalA && !isTotalB) return -1;
      if (isTotalB && !isTotalA) return 1;
      const isOtherA = /other/i.test(a!.terpene);
      const isOtherB = /other/i.test(b!.terpene);
      if (isOtherA && !isOtherB) return 1;
      if (isOtherB && !isOtherA) return -1;
      const aVal = Number.parseFloat(a!.percent);
      const bVal = Number.parseFloat(b!.percent);
      return bVal - aVal;
    });
}

function buildPackageDetails(
  coa: Record<string, unknown> | null,
  productCard: any,
  packageLabel?: string,
  facilityName?: string,
  facilityLicense?: string,
  confirmedDates?: ConfirmedDate[],
  hideBatch = false,
  hideHarvestDate = false,
  marketCode?: string,
  manualLabOverrides?: unknown,
): [string, string][] {
  const details: [string, string][] = [];
  const isColorado = marketCode === 'CO';

  // const purchasedAt = str((coa as any)?.receivedFromFacilityName);
  // if (purchasedAt) details.push(['Purchased At', purchasedAt]);

  // const location = str((coa as any)?.locationName);
  // if (location) details.push(['Location', location]);

  // const purchaseDate = formatDate((coa as any)?.receivedDateTime);
  // if (purchaseDate) details.push(['Purchase Date', purchaseDate]);

  const tag = str(packageLabel);
  if (tag) details.push(['Package Tag', tag]);

  const batch = str(coa?.batch);
  if (batch && !hideBatch) details.push(['Batch ID', batch]);

  if (facilityName) details.push(['Facility', facilityName]);
  if (facilityLicense) details.push(['Facility License', facilityLicense]);

  const unit = (coa as any)?.unit;
  const netWeight = str(unit?.weight);
  const weightUom = str(unit?.weightUnitOfMeasureName);
  if (netWeight && !isColorado) details.push(['Net Weight', weightUom ? `${netWeight} ${weightUom}` : netWeight]);

  details.push(...resolveLandingDates(coa, productCard, confirmedDates, hideHarvestDate, marketCode));

  const summary = effectivePackageLabSummary(coa, marketCode, manualLabOverrides);
  const testedDate = summary.multiple ? summary.testedDate : formatDate(summary.testedDate);
  if (testedDate) details.push([`Tested Date${summary.manualFields.includes('testedDate') ? ' (override)' : ''}`, testedDate]);

  const testedBy = summary.testedBy;
  if (testedBy) details.push([`Tested By${summary.manualFields.includes('testedBy') ? ' (override)' : ''}`, testedBy]);

  const labLicense = summary.licenseNumber;
  if (labLicense) details.push([`Laboratory License${summary.manualFields.includes('licenseNumber') ? ' (override)' : ''}`, labLicense]);

  return details;
}

export default function LandingPage({
  productCard,
  brandCard,
  coaCard,
  regulatoryCard,
  packageLabel,
  isStaged,
  stagedProducerName,
  stagedProductName,
  facilityName,
  facilityLicense,
  isOnRecall,
  publicHealthNoticeUrls,
  marketCode,
  confirmedDates,
  layout,
  issuanceId,
  landingIndex = 0,
}: LandingPageProps) {
  const location = useLocation();
  const regulatoryDescription = str(regulatoryCard?.description);
  const isMarkdownDescription = regulatoryCard?.descriptionFormat === DescriptionFormat.Markdown;
  const markdownDescription = useRemarkSync(isMarkdownDescription ? regulatoryDescription : '');
  const regulatoryDescriptionText = isMarkdownDescription
    ? descriptionFirstBlockText(markdownDescription)
    : regulatoryDescription;

  const brandName = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const fromQuery = str(params.get('brand') || params.get('brandName') || '');
    const fromCard = str(brandCard?.displayName);
    return fromQuery || fromCard || '';
  }, [location.search, brandCard?.displayName]);

  const coa = useMemo(() => {
    const raw = coaCard?.data;
    const coaDataRaw = (typeof raw === 'string' ? raw : raw ? JSON.stringify(raw) : '').trim();
    return parseCoaData(coaDataRaw);
  }, [coaCard?.data]);

  const coaFiles = useMemo(() => {
    const normalizeFileLinks = (filesData: unknown) => {
      let fileCandidates: unknown[] = [];
      if (Array.isArray(filesData)) {
        fileCandidates = filesData;
      } else if (typeof filesData === 'string') {
        const trimmed = filesData.trim();
        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) fileCandidates = parsed;
          } catch { /* ignore */ }
        }
        if (fileCandidates.length === 0 && trimmed) fileCandidates = [trimmed];
      }

      return fileCandidates
        .map(normalizePublicLandingUrl)
        .filter((file): file is string => file !== null);
    };

    let files = normalizeFileLinks(coaCard?.fileLink);
    if (files.length === 0) files = normalizeFileLinks((coa as any)?.fileLink);

    if (files.length === 0) {
      const fallbackCandidates = [
        coaCard?.url,
        coaCard?.coaUrl,
        (coa as any)?.url,
        (coa as any)?.coaUrl,
      ];
      const fallback = fallbackCandidates
        .map(normalizePublicLandingUrl)
        .find((file): file is string => file !== null);
      if (fallback) files = [fallback];
    }

    return files;
  }, [coaCard, coa]);

  const stagedColorado = isStaged && resolveLandingMarketCode(marketCode, regulatoryCard) === 'CO';
  const publicCoaFiles = usePublicCoaFiles(
    stagedColorado ? '' : issuanceId, landingIndex, coaFiles,
    isStaged === false && Array.isArray(coaCard?.fileLink),
  );

  const props = useMemo<RetailIdExpandedDesignPageProps>(() => {
    const productImageSrc = normalizePublicLandingUrl(productCard?.productImage) ?? undefined;
    const brandLogoUrl = normalizePublicLandingUrl(brandCard?.logoUrl);
    const rawRegulatoryHref = str(regulatoryCard?.websiteUrl) || str(regulatoryCard?.websiteLink);
    const regulatoryHref = normalizePublicLandingUrl(rawRegulatoryHref) ?? undefined;
    const complianceBodyName = extractComplianceBodyName(regulatoryDescriptionText);
    const effectiveMarketCode = resolveLandingMarketCode(marketCode, regulatoryCard);
    const marketCustomization = getMarketCustomization(effectiveMarketCode);
    const usesBatchTestResults = effectiveMarketCode === 'CO';
    const productBrandDisclaimer =
      effectiveMarketCode?.toUpperCase() === 'NY' &&
      regulatoryCard &&
      (str(regulatoryCard?.websiteUrl) || str(regulatoryCard?.websiteLink))
        ? '*Cannabis product and brand information is not provided by the Office of Cannabis Management'
        : undefined;
    const beResourceLinks = Array.isArray(regulatoryCard?.resourceLinks)
      ? regulatoryCard.resourceLinks
          .map((r: any) => ({
            label: str(r?.label),
            href: normalizePublicLandingUrl(str(r?.href) || str(r?.url)),
          }))
          .filter((r: { label: string; href: string | null }): r is { label: string; href: string } => Boolean(r.label && r.href))
      : [];
    const fallbackResources = regulatoryHref
      ? [{ label: `${complianceBodyName ?? 'Regulator'} Website`, href: regulatoryHref }]
      : [];
    const resources = beResourceLinks.length ? beResourceLinks : fallbackResources;
    const cannabinoidDisplayMode = getCannabinoidDisplayMode(coa);

    return {
      stagedSummary: isStaged === true && effectiveMarketCode === 'CO'
        ? { producerName: typeof stagedProducerName === 'string' ? stagedProducerName.trim() || undefined : undefined }
        : undefined,
      productImageSrc,
      productTitle: str(productCard?.productName)
        || (isStaged === true && effectiveMarketCode === 'CO' && typeof stagedProductName === 'string' ? stagedProductName.trim() : ''),
      brandText: brandName ? `by ${brandName}` : '',
      chips: [str(productCard?.strain)].filter(Boolean),
      fullDescription: str(productCard?.description),
      overviewRows: buildOverviewRows(coa, productCard, effectiveMarketCode),
      labTestStatus: buildLabTestStatus(coa, !usesBatchTestResults),
      batchTestResultsRequest: usesBatchTestResults
        ? { issuanceId: issuanceId || '', index: landingIndex, marketCode: effectiveMarketCode }
        : undefined,
      cannabinoids: buildCannabinoids(coa, cannabinoidDisplayMode, coaCard?.manualLabOverrides),
      cannabinoidDisplayMode,
      terpenes: buildTerpenes(coa, coaCard?.manualLabOverrides),
      packageDetails: buildPackageDetails(
        coa,
        productCard,
        packageLabel,
        facilityName,
        facilityLicense,
        confirmedDates,
        coaCard?.hideBatch === true,
        coaCard?.hideHarvestDate === true,
        effectiveMarketCode,
        coaCard?.manualLabOverrides,
      ),
      resources,
      coaFiles,

      // Brand tab
      brandName,
      brandTagline: str(brandCard?.tagline),
      brandDescription: str(brandCard?.description),
      brandSlides: brandLogoUrl
        ? [{ src: brandLogoUrl, alt: `${brandName} logo` }]
        : undefined,
      brandSections: [],
      brandPurchaseUrl: normalizePublicLandingUrl(brandCard?.purchaseUrl) ?? undefined,
      brandWebsiteUrl: normalizePublicLandingUrl(brandCard?.websiteUrl) ?? undefined,

      // Regulatory
      stateWebsiteHref: regulatoryHref,
      stateLogoSrc: marketCustomization.stateLogoSrcOverride
        ?? normalizePublicLandingUrl(regulatoryCard?.logoUrl)
        ?? undefined,
      stateDescription: str(regulatoryCard?.description) || undefined,
      stateDescriptionFormat: regulatoryCard?.descriptionFormat ?? DescriptionFormat.PlainText,
      verifiedBadgeSrc: isOnRecall ? undefined : marketCustomization.verifiedBadgeSrc,
      recallMessage: isOnRecall
        ? (marketCustomization.recallBannerVariant === 'colorado'
          ? 'This product is under an administrative recall issued by the Colorado Marijuana Enforcement Division. Do not use this product.'
          : marketCustomization.recallBannerVariant === 'urgent'
          ? 'This product is subject to a statewide recall.'
          : `This product has been recalled. Please visit ${complianceBodyName ? `${complianceBodyName}'s` : "the regulator's"} website for more information.`)
        : undefined,
      recallVariant: marketCustomization.recallBannerVariant,
      recallMoreInfoHref: marketCustomization.recallMoreInfoHref,
      publicHealthNoticeUrls: effectiveMarketCode === 'CO' ? publicHealthNoticeUrls ?? undefined : undefined,
      productBrandDisclaimer,
      licenseeNotice: marketCustomization.licenseeNotice,
    };
  }, [productCard, brandCard, coa, coaCard?.hideBatch, coaCard?.hideHarvestDate, coaCard?.manualLabOverrides, coaFiles, regulatoryCard, regulatoryDescriptionText, brandName, packageLabel, isStaged, stagedProducerName, stagedProductName, facilityName, facilityLicense, isOnRecall, publicHealthNoticeUrls, marketCode, confirmedDates, issuanceId, landingIndex]);

  return <RetailIdExpandedDesignPage {...props} layout={layout} coaResults={issuanceId ? publicCoaFiles : undefined} />;
}
